const FOLDED: Record<string, string> = {
    ł: 'l',
    đ: 'd',
    ø: 'o',
    ß: 'ss',
    æ: 'ae',
    œ: 'oe',
    ı: 'i',
};

const COMBINING_MARK = /\p{M}/gu;

const foldChar = (char: string): string => {
    const lower = char.toLowerCase();
    return FOLDED[lower] ?? lower.normalize('NFD').replace(COMBINING_MARK, '');
};

export interface FoldedText {
    text: string;
    origin: number[];
}

export const foldText = (input: string): FoldedText => {
    let text = '';
    const origin: number[] = [];
    let index = 0;
    for (const char of input) {
        const folded = foldChar(char);
        for (let i = 0; i < folded.length; i++) {
            origin.push(index);
        }
        text += folded;
        index += char.length;
    }
    origin.push(index);
    return {text, origin};
};

export const foldQuery = (query: string): string => foldText(query.trim().replace(/\s+/g, ' ')).text;

export interface TextRange {
    start: number;
    end: number;
}

export const findInText = (input: string, foldedQuery: string): TextRange[] => {
    if (!foldedQuery) return [];
    const folded = foldText(input);
    const ranges: TextRange[] = [];
    let from = 0;
    for (;;) {
        const at = folded.text.indexOf(foldedQuery, from);
        if (at === -1) break;
        const end = at + foldedQuery.length;
        ranges.push({start: folded.origin[at], end: folded.origin[end] ?? input.length});
        from = end;
    }
    return ranges;
};

export interface SearchMatch extends TextRange {
    segmentIndex: number;
}

export const findMatches = (segments: readonly {text: string}[], query: string): SearchMatch[] => {
    const foldedQuery = foldQuery(query);
    if (!foldedQuery) return [];
    return segments.flatMap((segment, segmentIndex) =>
        findInText(segment.text, foldedQuery).map((range) => ({...range, segmentIndex})));
};

export const STEM_LENGTH = 5;

const MIN_STEM_WORD = 3;

const WORD = /[\p{L}\p{N}]+/gu;

export const topicStems = (label: string): string[] => {
    const words = foldText(label).text.match(WORD) ?? [];
    const meaningful = words.filter((word) => word.length >= MIN_STEM_WORD);
    return [...new Set((meaningful.length > 0 ? meaningful : words).map((word) => word.slice(0, STEM_LENGTH)))];
};

export const findStemsInText = (input: string, stems: readonly string[]): TextRange[] => {
    if (stems.length === 0) return [];
    const folded = foldText(input);
    const ranges: TextRange[] = [];
    for (const word of folded.text.matchAll(WORD)) {
        if (!stems.some((stem) => word[0].startsWith(stem))) continue;
        const at = word.index ?? 0;
        ranges.push({start: folded.origin[at], end: folded.origin[at + word[0].length] ?? input.length});
    }
    return ranges;
};

export const findStemMatches = (segments: readonly {text: string}[], label: string): SearchMatch[] => {
    const stems = topicStems(label);
    if (stems.length === 0) return [];
    return segments.flatMap((segment, segmentIndex) =>
        findStemsInText(segment.text, stems).map((range) => ({...range, segmentIndex})));
};

export interface TextPart {
    text: string;
    match: boolean;
    current: boolean;
}

export const splitByRanges = (input: string, ranges: readonly TextRange[], currentStart: number | null): TextPart[] => {
    if (ranges.length === 0) return [{text: input, match: false, current: false}];
    const parts: TextPart[] = [];
    let cursor = 0;
    for (const range of ranges) {
        if (range.start > cursor) parts.push({text: input.slice(cursor, range.start), match: false, current: false});
        parts.push({text: input.slice(range.start, range.end), match: true, current: range.start === currentStart});
        cursor = range.end;
    }
    if (cursor < input.length) parts.push({text: input.slice(cursor), match: false, current: false});
    return parts;
};
